import { useMemo } from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../../shared/helpers';
import { Badge, BadgeColors } from '../../../generic';
import { DetailsPanelList } from '../../../generic/DetailsPanel';
import DynamicShowMoreList from '../../../generic/DynamicShowMoreList';
import MiniPercentageBar from '../../../generic/MiniPercentageBar';
import type { SnippetDetails } from '../../../../../shared/snippets';

import DetailsSection from './DetailsSection';

type Props = {
  snippet: SnippetDetails;
};

/**
 * The Match Confidence drawer details.
 */
const MatchConfidence: VoidFunctionComponent<Props> = ({ snippet }) => {
  const otherVersionElements = useMemo(() => snippet.otherVersions.map((otherVersion) => (
    <span key={otherVersion.version}>
      {otherVersion.version}&nbsp;
      <Badge color={BadgeColors.LightGray}>
        {otherVersion.matchCount} {plural(otherVersion.matchCount, 'file', 'files')}
      </Badge>
    </span>
  )), [snippet.otherVersions]);
  const rows = useMemo(() => {
    return [
      {
        label: 'Percentage of matching code',
        content: (
          <MiniPercentageBar percent={Math.round(snippet.highestMatchPercentage * 100)} />
        ),
      },
      {
        label: 'Number of matching files',
        content: (
          <span>
            {snippet.matchCount}
          </span>
        ),
      },
      {
        label: 'Potential version matches',
        content: (
          <span>
            {snippet.matchCount}
          </span>
        ),
      },
      {
        label: 'Other version matches',
        content: (
          snippet.otherVersions.length === 0 ? <span className="italic">(No other versions)</span> : (
            <DynamicShowMoreList list={otherVersionElements} lineHeight={25} isRightAligned hasLargeGap />
          )
        ),
      },
    ];
  }, [snippet, otherVersionElements]);

  return (
    <DetailsSection heading="Match Confidence">
      <DetailsPanelList rows={rows} />
    </DetailsSection>
  );
};

export default MatchConfidence;
